import { useCallback, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FilterableSelect } from '../common/FilterableSelect';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import HubIcon from '@mui/icons-material/Hub';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useDeleteWorldElement,
  useReparentWorldElement,
  useUpdateWorldElement,
  useUpdateWorldElementImage,
  WORLD_ELEMENT_TYPES,
  type WorldElementDto,
} from '../../api/queries/worlds';
import { createApiClient } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import { useUploadImage } from '../../api/queries/media';
import { AppImage } from '../common/AppImage';
import { GenerationModal } from '../ai/GenerationModal';
import { CardAttributeEditor } from './CardAttributeEditor';
import {
  CARD_ATTRIBUTE_SCHEMAS,
  ELEMENT_ASSOCIATION_TYPES,
} from './cardAttributeSchemas';

interface EntityOption {
  id: string;
  name: string;
  entityType: string;
}

// ---------------------------------------------------------------------------
// Structured fields per element type
// ---------------------------------------------------------------------------

/**
 * Type-specific structured field definitions. Each field is stored in the
 * description as a markdown section: `## Heading\nContent`. When the element
 * type has structured fields we parse them out for display and merge back on
 * save.
 */
const STRUCTURED_FIELDS: Record<string, string[]> = {
  magic_system: ['Rules', 'Limitations', 'Cost', 'Source'],
  being: ['Appearance', 'Culture', 'History', 'Relations'],
  creature: ['Appearance', 'Abilities', 'Habitat', 'Behavior', 'Diet'],
  culture: ['Customs', 'Beliefs', 'Taboos', 'Celebrations'],
  country: ['Government', 'Economy', 'Military', 'Demographics'],
  deity: ['Domains', 'Worship', 'Mythology', 'Conflicts'],
  ecosystem: ['Biomes', 'Flora', 'Fauna', 'Dangers'],
  history: ['Major Events', 'Eras', 'Conflicts', 'Turning Points'],
  language: ['Dialects', 'Script', 'Notable Phrases'],
  mythology: ['Origin Stories', 'Prophecies', 'Legends'],
  location: ['Geography', 'Climate', 'Population', 'Notable Features'],
  technology: ['Capabilities', 'Limitations', 'Availability', 'Era'],
  weapon: ['Lore', 'Crafting Requirements', 'Notable Wielders', 'Variants'],
  spell: ['Lore', 'Visual Description', 'Incantation', 'Counters'],
  faction: ['Purpose', 'Hierarchy', 'Recruitment', 'Rivals'],
  society: ['Classes', 'Tensions', 'Daily Life'],
  economy: ['Currency', 'Resources', 'Trade'],
  government: ['Structure', 'Leadership', 'Laws'],
  law: ['Enforcement', 'Courts', 'Penalties', 'Grey Areas'],
  ability: ['Types', 'Training', 'Risks', 'Limitations'],
  religion: ['Beliefs', 'Rituals', 'Clergy', 'Sacred Sites'],
  potion: ['Effect', 'Duration', 'Ingredients', 'Side Effects'],
};

// ---------------------------------------------------------------------------
// Markdown section helpers
// ---------------------------------------------------------------------------

interface ParsedSections {
  /** Free-form text before the first ## heading */
  preamble: string;
  /** Ordered map of heading -> body */
  sections: Map<string, string>;
}

function parseSections(raw: string): ParsedSections {
  const lines = raw.split('\n');
  let preamble = '';
  const sections = new Map<string, string>();
  let currentHeading: string | null = null;
  const buffer: string[] = [];

  for (const line of lines) {
    const match = /^## (.+)$/.exec(line);
    if (match) {
      if (currentHeading !== null) {
        sections.set(currentHeading, buffer.join('\n').trim());
      } else {
        preamble = buffer.join('\n').trim();
      }
      currentHeading = match[1].trim();
      buffer.length = 0;
    } else {
      buffer.push(line);
    }
  }

  if (currentHeading !== null) {
    sections.set(currentHeading, buffer.join('\n').trim());
  } else {
    preamble = buffer.join('\n').trim();
  }

  return { preamble, sections };
}

function serializeSections(
  preamble: string,
  fields: string[],
  values: Map<string, string>,
): string {
  const parts: string[] = [];
  if (preamble.trim()) {
    parts.push(preamble.trim());
  }
  for (const field of fields) {
    const val = values.get(field)?.trim();
    if (val) {
      parts.push(`## ${field}\n${val}`);
    }
  }
  return parts.join('\n\n');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface WorldElementEditorProps {
  element: WorldElementDto;
  worldId: string;
  onUpdate: (updates: Partial<WorldElementDto>) => void;
  onGenerateImage: () => void;
}

export function WorldElementEditor({
  element,
  worldId,
  onUpdate,
  onGenerateImage,
}: WorldElementEditorProps) {
  const { getToken } = useAuth();
  const update = useUpdateWorldElement(worldId);
  const remove = useDeleteWorldElement(worldId);
  const reparent = useReparentWorldElement(worldId);
  const updateImage = useUpdateWorldElementImage(worldId);
  const uploadImage = useUploadImage();

  // Entity linking
  const { data: entities = [] } = useQuery<EntityOption[]>({
    queryKey: ['entities-for-picker'],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get('api/v2/entities')
        .json<{ data: EntityOption[] }>();
      return resp.data ?? [];
    },
  });

  const [linkedEntityIds, setLinkedEntityIds] = useState<string[]>(
    () => element.linkedEntityIds ?? [],
  );

  const linkedEntities = useMemo(
    () => entities.filter((e) => linkedEntityIds.includes(e.id)),
    [entities, linkedEntityIds],
  );

  const availableEntities = useMemo(
    () => entities.filter((e) => !linkedEntityIds.includes(e.id)),
    [entities, linkedEntityIds],
  );

  const handleLinkEntity = useCallback(
    (_: unknown, entity: EntityOption | null) => {
      if (entity) {
        setLinkedEntityIds((prev) => [...prev, entity.id]);
      }
    },
    [],
  );

  const handleUnlinkEntity = useCallback((entityId: string) => {
    setLinkedEntityIds((prev) => prev.filter((id) => id !== entityId));
  }, []);

  const queryClient = useQueryClient();

  // Card attributes (game stats) from metadata
  const [cardAttrs, setCardAttrs] = useState<Record<string, unknown>>(() => {
    const md = element.metadata as Record<string, unknown> | null;
    if (
      md &&
      typeof md === 'object' &&
      'card' in md &&
      typeof md.card === 'object'
    ) {
      return (md.card ?? {}) as Record<string, unknown>;
    }
    return {};
  });
  const [cardExpanded, setCardExpanded] = useState(
    Object.values(cardAttrs).some(
      (v) => v !== undefined && v !== null && v !== '',
    ),
  );

  // Element-to-element associations
  interface AssocDto {
    id: string;
    relatedElementId: string;
    relatedElementName: string;
    associationType: string;
    notes?: string | null;
    direction: string;
  }
  const { data: associations = [] } = useQuery<AssocDto[]>({
    queryKey: ['element-associations', element.id],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/world-elements/${element.id}/associations`)
        .json<{ data: AssocDto[] }>();
      return resp.data ?? [];
    },
  });

  const addAssociation = useMutation({
    mutationFn: async (input: {
      targetElementId: string;
      associationType: string;
    }) => {
      const client = createApiClient(getToken);
      await client.post(`api/v2/world-elements/${element.id}/associations`, {
        json: input,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['element-associations', element.id],
      });
    },
  });

  const removeAssociation = useMutation({
    mutationFn: async (assocId: string) => {
      const client = createApiClient(getToken);
      await client.delete(`api/v2/world-element-associations/${assocId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['element-associations', element.id],
      });
    },
  });

  // World elements for association picker
  const { data: worldElements = [] } = useQuery<
    { id: string; name: string; elementType: string }[]
  >({
    queryKey: ['world-elements-for-assoc', worldId],
    queryFn: async () => {
      const client = createApiClient(getToken);
      const resp = await client
        .get(`api/v2/worlds/${worldId}`)
        .json<{
          data: {
            elements: { id: string; name: string; elementType: string }[];
          };
        }>();
      return resp.data?.elements ?? [];
    },
  });

  const availableForAssoc = useMemo(
    () =>
      worldElements.filter(
        (e) =>
          e.id !== element.id &&
          !associations.some((a) => a.relatedElementId === e.id),
      ),
    [worldElements, element.id, associations],
  );

  const [assocType, setAssocType] = useState('related');

  const [name, setName] = useState(element.name);
  const [type, setType] = useState(element.elementType);

  // Parse description into preamble + structured sections
  const initial = useMemo(
    () => parseSections(element.description ?? ''),
    [element.description],
  );
  const [preamble, setPreamble] = useState(initial.preamble);

  const fields = STRUCTURED_FIELDS[type] ?? [];
  const [sectionValues, setSectionValues] = useState<Map<string, string>>(
    () => {
      const m = new Map<string, string>();
      for (const f of fields) {
        m.set(f, initial.sections.get(f) ?? '');
      }
      return m;
    },
  );

  const [structuredExpanded, setStructuredExpanded] = useState(
    fields.some((f) => (sectionValues.get(f) ?? '').trim().length > 0),
  );

  // When type changes, re-derive structured fields
  const handleTypeChange = useCallback(
    (newType: string) => {
      setType(newType);
      const newFields = STRUCTURED_FIELDS[newType] ?? [];
      setSectionValues((prev) => {
        const m = new Map<string, string>();
        for (const f of newFields) {
          m.set(f, prev.get(f) ?? initial.sections.get(f) ?? '');
        }
        return m;
      });
    },
    [initial.sections],
  );

  const setSectionValue = useCallback((field: string, value: string) => {
    setSectionValues((prev) => {
      const m = new Map(prev);
      m.set(field, value);
      return m;
    });
  }, []);

  // GenerationModal state
  const [genOpen, setGenOpen] = useState(false);
  const [genMode, setGenMode] = useState<'text' | 'image'>('text');

  const buildDescriptionPrompt = (): string => {
    const typeLabel =
      WORLD_ELEMENT_TYPES.find((t) => t.value === type)?.label ?? type;
    return `Generate a detailed description for a ${typeLabel} called "${name}" in a fantasy world. Include relevant details about its characteristics, history, and significance.`;
  };

  const handleSave = () => {
    const currentFields = STRUCTURED_FIELDS[type] ?? [];
    const description =
      currentFields.length > 0
        ? serializeSections(preamble, currentFields, sectionValues)
        : preamble;

    // Build metadata: preserve existing fields, update card attributes
    const existingMeta = (element.metadata ?? {}) as Record<string, unknown>;
    const hasCardSchema = !!CARD_ATTRIBUTE_SCHEMAS[type];
    const metadata = hasCardSchema
      ? { ...existingMeta, card: cardAttrs }
      : existingMeta;

    const updates = {
      id: element.id,
      name: name.trim(),
      elementType: type,
      description,
      imageUrl: element.imageUrl,
      linkedEntityIds,
      metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
    };
    update.mutateAsync(updates);
    onUpdate(updates);
  };

  const handleRemoveImage = () => {
    updateImage.mutate({ elementId: element.id, imageUrl: null });
  };

  const handleUploadImage = async (file: File) => {
    const uploaded = await uploadImage.mutateAsync(file);
    if (uploaded?.url) {
      updateImage.mutate({ elementId: element.id, imageUrl: uploaded.url });
    }
  };

  const fileInputRef = useCallback((input: HTMLInputElement | null) => {
    if (input) {
      input.value = '';
    }
  }, []);

  const handleGenerateDescription = () => {
    setGenMode('text');
    setGenOpen(true);
  };

  const handleGenerateImage = () => {
    setGenMode('image');
    setGenOpen(true);
    onGenerateImage();
  };

  const typeLabel =
    WORLD_ELEMENT_TYPES.find((t) => t.value === type)?.label ?? type;

  return (
    <Stack spacing={2} sx={{ maxWidth: 800 }}>
      {/* Header */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="h5" sx={{ flex: 1 }}>
          Edit element
        </Typography>
        <IconButton
          onClick={() => remove.mutate(element.id)}
          aria-label="delete"
          color="error"
        >
          <DeleteIcon />
        </IconButton>
      </Stack>

      {/* Name */}
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
      />

      {/* Type */}
      <FilterableSelect
        options={WORLD_ELEMENT_TYPES.map((t) => ({
          value: t.value,
          label: t.label,
        }))}
        value={type}
        onChange={(newValue) => handleTypeChange(newValue)}
        label="Type"
        allowEmpty={false}
        fullWidth={true}
      />

      {/* Description with AI generate button */}
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Description
          </Typography>
          <Button
            size="small"
            startIcon={<AutoFixHighIcon />}
            onClick={handleGenerateDescription}
            sx={{ textTransform: 'none' }}
          >
            Generate description
          </Button>
        </Stack>
        <TextField
          value={preamble}
          onChange={(e) => setPreamble(e.target.value)}
          multiline
          minRows={4}
          fullWidth
          placeholder={`Describe this ${typeLabel.toLowerCase()}...`}
        />
      </Box>

      {/* Structured fields (collapsible) */}
      {fields.length > 0 && (
        <Accordion
          expanded={structuredExpanded}
          onChange={(_, expanded) => setStructuredExpanded(expanded)}
          disableGutters
          elevation={0}
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2">{typeLabel} details</Typography>
              <Chip
                size="small"
                label={`${fields.filter((f) => (sectionValues.get(f) ?? '').trim()).length}/${fields.length}`}
                variant="outlined"
              />
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={2}>
              {fields.map((field) => (
                <TextField
                  key={field}
                  label={field}
                  value={sectionValues.get(field) ?? ''}
                  onChange={(e) => setSectionValue(field, e.target.value)}
                  multiline
                  minRows={2}
                  fullWidth
                />
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Card attributes (game stats) */}
      <CardAttributeEditor
        elementType={type}
        values={cardAttrs}
        onChange={setCardAttrs}
        expanded={cardExpanded}
        onExpandedChange={setCardExpanded}
      />

      {/* Element-to-element associations */}
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Associated elements</Typography>
          <HubIcon fontSize="small" color="action" />
        </Stack>
        {associations.length > 0 && (
          <Stack spacing={0.5} sx={{ mb: 1 }}>
            {associations.map((a) => (
              <Stack
                key={a.id}
                direction="row"
                spacing={0.5}
                alignItems="center"
              >
                <Chip
                  label={a.relatedElementName}
                  size="small"
                  onDelete={() => removeAssociation.mutate(a.id)}
                />
                <Typography variant="caption" color="text.secondary">
                  {ELEMENT_ASSOCIATION_TYPES.find(
                    (t) => t.value === a.associationType,
                  )?.label ?? a.associationType}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
        <Stack direction="row" spacing={1}>
          <FilterableSelect
            label="Relation"
            value={assocType}
            onChange={(v) => setAssocType(v || 'related')}
            options={ELEMENT_ASSOCIATION_TYPES}
            allowEmpty={false}
            sx={{ minWidth: 140 }}
          />
          <Autocomplete
            options={availableForAssoc}
            getOptionLabel={(opt) => opt.name}
            groupBy={(opt) => opt.elementType}
            value={null}
            onChange={(_, target) => {
              if (target) {
                addAssociation.mutate({
                  targetElementId: target.id,
                  associationType: assocType,
                });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Link to element..."
                size="small"
              />
            )}
            size="small"
            blurOnSelect
            clearOnBlur
            sx={{ flex: 1 }}
          />
        </Stack>
      </Box>

      {/* Image section */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Image
        </Typography>
        {element.imageUrl ? (
          <Stack spacing={1}>
            <AppImage
              src={element.imageUrl}
              alt={element.name}
              width="100%"
              height={200}
              objectFit="cover"
              sx={{ borderRadius: 1 }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={handleRemoveImage}
              >
                Remove image
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={handleGenerateImage}
            >
              Generate image
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ImageIcon />}
              component="label"
              disabled={uploadImage.isPending}
            >
              {uploadImage.isPending ? 'Uploading...' : 'Upload'}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUploadImage(file);
                }}
              />
            </Button>
          </Stack>
        )}
      </Box>

      {/* Entity linking */}
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Linked entities</Typography>
          <LinkIcon fontSize="small" color="action" />
        </Stack>
        {linkedEntities.length > 0 && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 1, flexWrap: 'wrap', gap: 0.5 }}
          >
            {linkedEntities.map((entity) => (
              <Chip
                key={entity.id}
                label={entity.name}
                size="small"
                onDelete={() => handleUnlinkEntity(entity.id)}
              />
            ))}
          </Stack>
        )}
        <Autocomplete
          options={availableEntities}
          getOptionLabel={(opt) => opt.name}
          groupBy={(opt) => opt.entityType}
          value={null}
          onChange={handleLinkEntity}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search entities to link..."
              size="small"
            />
          )}
          size="small"
          blurOnSelect
          clearOnBlur
        />
      </Box>

      {/* Action bar */}
      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          disabled={!name.trim() || update.isPending}
          onClick={handleSave}
        >
          Save
        </Button>
        {element.parentId && (
          <Button
            variant="outlined"
            onClick={() =>
              reparent.mutate({ id: element.id, newParentId: null })
            }
          >
            Move to root
          </Button>
        )}
      </Stack>

      {/* Generation modal */}
      <GenerationModal
        open={genOpen}
        onClose={() => setGenOpen(false)}
        mode={genMode}
        seedPrompt={
          genMode === 'text'
            ? buildDescriptionPrompt()
            : `${name}, ${typeLabel}, ${preamble.slice(0, 200)}`
        }
      />
    </Stack>
  );
}
